import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import "./StaffRequisitions.css";

const emptyItem = () => ({
  description: "",
  quantity: 1,
  unit: "item",
  unit_price: "",
});
const emptyForm = () => ({
  request_date: new Date().toISOString().slice(0, 10),
  required_date: "",
  purpose: "",
  budget_line_id: "",
  branch_id: "",
  cost_center_id: "",
  preferred_payee: "",
  procurement_notes: "",
  items: [emptyItem()],
});
const unwrap = (response) => response?.data || response || [];
const money = (value) =>
  `TZS ${Number(value || 0).toLocaleString("en-TZ", { minimumFractionDigits: 2 })}`;

export default function StaffRequisitions() {
  const { hasPermission, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestedTab = searchParams.get("tab");
  const activeTab = requestedTab === "new" && hasPermission("requisitions.create") ? "new" : "register";
  const [rows, setRows] = useState([]);
  const [metadata, setMetadata] = useState({
    budget_lines: [],
    branch_budget_statuses: [],
    branches: [],
    cost_centers: [],
    cash_accounts: [],
    requester: null,
  });
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentTitle, setDocumentTitle] = useState("");
  const [convertForm, setConvertForm] = useState({
    payee: "",
    cash_account_id: "",
    payment_method: "bank_transfer",
    payment_date: new Date().toISOString().slice(0, 10),
    payment_reference: "",
  });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState({ organization_name: "Tanzania Revenue Workers Union", organization_short_name: "TAREWU", logo_url: "/TAREWU_logo-2.png" });

  const load = async () => {
    try {
      const [list, meta, brand] = await Promise.all([
        api.getStaffRequisitions(),
        api.getStaffRequisitionMetadata(),
        api.getBrandingSettings().catch(() => ({ data: null })),
      ]);
      setRows(Array.isArray(unwrap(list)) ? unwrap(list) : []);
      const metadataResponse = unwrap(meta);
      setMetadata(metadataResponse);
      if (brand?.data) setBranding((current) => ({ ...current, ...brand.data }));
      setForm((current) => ({
        ...current,
        branch_id: current.branch_id || metadataResponse.requester?.branch_id || "",
      }));
    } catch (e) {
      setError(e.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const total = useMemo(
    () =>
      form.items.reduce(
        (sum, item) =>
          sum + Number(item.quantity || 0) * Number(item.unit_price || 0),
        0,
      ),
    [form.items],
  );
  const selectedBudgetLine = metadata.budget_lines.find(
    (line) => String(line.id) === String(form.budget_line_id),
  );
  const branchBudgetLines = metadata.budget_lines.filter(
    (line) => String(line.branch_id) === String(form.branch_id),
  );
  const branchPendingBudgets = (metadata.branch_budget_statuses || []).filter(
    (item) =>
      String(item.branch_id) === String(form.branch_id) &&
      item.status !== "approved",
  );
  const selectedBudgetMeta = selected
    ? metadata.budget_lines.find((line) => String(line.id) === String(selected.budget_line_id))
    : null;
  const selectedBranch = selected
    ? metadata.branches.find((branch) => String(branch.id) === String(selected.branch_id))
    : null;
  const brandingLogo = branding.logo_url?.startsWith("http")
    ? branding.logo_url
    : `${api.baseUrl.replace(/\/api\/?$/, "")}${branding.logo_url || "/TAREWU_logo-2.png"}`;

  const chooseBudget = (id) => {
    const line = metadata.budget_lines.find(
      (item) => String(item.id) === String(id),
    );
    setForm({
      ...form,
      budget_line_id: id,
      cost_center_id: line?.cost_center_id || "",
    });
  };
  const changeItem = (index, field, value) =>
    setForm({
      ...form,
      items: form.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    });
  const reset = () => {
    setEditingId(null);
    setForm(emptyForm());
  };
  const selectTab = (tab) => {
    setSearchParams(tab === "new" ? { tab: "new" } : { tab: "register" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (editingId) await api.updateStaffRequisition(editingId, form);
      else await api.createStaffRequisition(form);
      setNotice(
        editingId ? "Requisition updated." : "Requisition saved as draft.",
      );
      reset();
      await load();
      selectTab("register");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const edit = (row) => {
    setEditingId(row.id);
    setForm({
      request_date: row.request_date,
      required_date: row.required_date || "",
      purpose: row.purpose,
      budget_line_id: row.budget_line_id,
      branch_id: row.branch_id || "",
      cost_center_id: row.cost_center_id || "",
      preferred_payee: row.preferred_payee || "",
      procurement_notes: row.procurement_notes || "",
      items: row.items.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
      })),
    });
    selectTab("new");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const action = async (row, name) => {
    let reason = null;
    if (name === "reject") {
      reason = window.prompt("Enter rejection reason:");
      if (!reason) return;
    }
    if (name === "cancel" && !window.confirm("Cancel this requisition?"))
      return;
    try {
      await api.transitionStaffRequisition(row.id, name, reason);
      setNotice(`Requisition ${name} completed.`);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const open = async (row) => {
    setSelected(row);
    setConvertForm((current) => ({
      ...current,
      payee: row.preferred_payee || "",
    }));
    try {
      const response = await api.getStaffRequisitionDocuments(row.id);
      setDocuments(unwrap(response));
    } catch (e) {
      setError(e.message);
    }
  };
  const upload = async (event) => {
    event.preventDefault();
    if (!documentFile || !documentTitle || !selected) return;
    try {
      await api.uploadStaffRequisitionDocument(
        selected.id,
        documentFile,
        documentTitle,
      );
      const response = await api.getStaffRequisitionDocuments(selected.id);
      setDocuments(unwrap(response));
      setDocumentFile(null);
      setDocumentTitle("");
      setNotice("Supporting document uploaded.");
    } catch (e) {
      setError(e.message);
    }
  };
  const requisitionPdf = async (row, preview = true) => {
    try { await api.staffRequisitionPdf(row.id, preview); }
    catch (e) { setError(e.message); }
  };
  const convert = async (event) => {
    event.preventDefault();
    try {
      await api.convertStaffRequisition(selected.id, convertForm);
      setNotice(
        "Payment Request created. Open Accounting → Payments to continue.",
      );
      setSelected(null);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="requisitions-page">
      <div className="requisitions-header">
        <button
          type="button"
          className="requisitions-back"
          aria-label="Back"
          onClick={() =>
            activeTab === "new"
              ? selectTab("register")
              : navigate(hasPermission("finance.read") ? "/accounting/payables" : "/")
          }
        >
          ←
        </button>
        <div>
          <span className="eyebrow">EXPENDITURE CONTROL</span>
          <h1>Requisition Register</h1>
          <p>
            Request goods or services against an approved budget before Finance
            makes payment.
          </p>
        </div>
        <div className="header-total">
          <small>{activeTab === "new" ? "Current draft total" : "Requisitions listed"}</small>
          <strong>{activeTab === "new" ? money(total) : rows.length}</strong>
        </div>
      </div>
      <nav className="requisition-tabs" aria-label="Requisition sections">
        <button className={activeTab === "register" ? "active" : ""} onClick={() => selectTab("register")}>Requisition Register</button>
        {hasPermission("requisitions.create") && <button className={activeTab === "new" ? "active" : ""} onClick={() => selectTab("new")}>+ New Requisition</button>}
      </nav>
      {error && (
        <div className="message error">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}
      {notice && (
        <div className="message success">
          {notice}
          <button onClick={() => setNotice("")}>×</button>
        </div>
      )}

      {activeTab === "new" && hasPermission("requisitions.create") && (
        <form className="requisition-form" onSubmit={save}>
          <div className="section-heading">
            <div>
              <h2>{editingId ? "Edit requisition" : "New requisition"}</h2>
              <p>Items determine the requested total automatically.</p>
            </div>
            {editingId && (
              <button type="button" className="light-button" onClick={reset}>
                Cancel edit
              </button>
            )}
          </div>
          <div className="form-grid">
            <label>
              Request date
              <input
                required
                type="date"
                value={form.request_date}
                onChange={(e) =>
                  setForm({ ...form, request_date: e.target.value })
                }
              />
            </label>
            <label>
              Requested by / Staff Name
              <input
                readOnly
                value={metadata.requester?.name || "Current staff"}
              />
            </label>
            <label>
              Needed on
              <input
                type="date"
                value={form.required_date}
                onChange={(e) =>
                  setForm({ ...form, required_date: e.target.value })
                }
              />
            </label>
            <label className="wide">
              Purpose
              <input
                required
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                placeholder="Why are these goods or services needed?"
              />
            </label>
            <label className="wide">
              Branch
              <select
                required
                disabled
                value={form.branch_id}
              >
                <option value="">Complete Staff Profile first</option>
                {metadata.branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <small>Branch is taken from your Staff Profile.</small>
            </label>
            <label className="wide">
              Approved budget line
              <select
                required
                disabled={!form.branch_id}
                value={form.budget_line_id}
                onChange={(e) => chooseBudget(e.target.value)}
              >
                <option value="">
                  {form.branch_id
                    ? "Select budget line"
                    : "Select branch first"}
                </option>
                {branchBudgetLines.map((line) => (
                  <option key={line.id} value={line.id}>
                    {line.branch_code} · {line.budget_name} · {line.account_code} {line.account_name}{" "}
                    · Available {money(line.availability?.available_amount)}
                  </option>
                ))}
              </select>
              {form.branch_id && branchBudgetLines.length === 0 && (
                <small className="budget-warning">
                  {branchPendingBudgets.length > 0
                    ? `Expense budget exists for this branch but is awaiting approval (${branchPendingBudgets.map((item) => item.status).join(", ")}). Submit and approve it before creating a requisition.`
                    : "No approved expense budget is available for this branch."}
                </small>
              )}
            </label>
            <label>
              Cost centre
              <select value={form.cost_center_id} disabled>
                <option value="">None</option>
                {metadata.cost_centers.map((centre) => (
                  <option key={centre.id} value={centre.id}>
                    {centre.code} — {centre.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Suggested payee
              <input
                value={form.preferred_payee}
                onChange={(e) =>
                  setForm({ ...form, preferred_payee: e.target.value })
                }
                placeholder="Supplier or staff name"
              />
            </label>
            <label>
              Procurement notes
              <input
                value={form.procurement_notes}
                onChange={(e) =>
                  setForm({ ...form, procurement_notes: e.target.value })
                }
                placeholder="Quotation or procurement details"
              />
            </label>
          </div>
          {selectedBudgetLine && (
            <div
              className={`budget-check ${total > Number(selectedBudgetLine.availability?.available_amount) ? "over" : ""}`}
            >
              <span>
                Available budget:{" "}
                <b>
                  {money(selectedBudgetLine.availability?.available_amount)}
                </b>
              </span>
              <span>
                After request:{" "}
                <b>
                  {money(
                    Number(selectedBudgetLine.availability?.available_amount) -
                      total,
                  )}
                </b>
              </span>
            </div>
          )}
          <div className="items">
            <div className="item-row item-head">
              <span>Description</span>
              <span>Qty</span>
              <span>Unit</span>
              <span>Unit price</span>
              <span>Total</span>
              <span></span>
            </div>
            {form.items.map((item, index) => (
              <div className="item-row" key={index}>
                <input
                  required
                  value={item.description}
                  onChange={(e) =>
                    changeItem(index, "description", e.target.value)
                  }
                  placeholder="Item or service"
                />
                <input
                  required
                  min="0.01"
                  step="0.01"
                  type="number"
                  value={item.quantity}
                  onChange={(e) =>
                    changeItem(index, "quantity", e.target.value)
                  }
                />
                <input
                  required
                  value={item.unit}
                  onChange={(e) => changeItem(index, "unit", e.target.value)}
                />
                <input
                  required
                  min="0"
                  step="0.01"
                  type="number"
                  value={item.unit_price}
                  onChange={(e) =>
                    changeItem(index, "unit_price", e.target.value)
                  }
                />
                <strong>
                  {money(
                    Number(item.quantity || 0) * Number(item.unit_price || 0),
                  )}
                </strong>
                <button
                  type="button"
                  disabled={form.items.length === 1}
                  onClick={() =>
                    setForm({
                      ...form,
                      items: form.items.filter((_, i) => i !== index),
                    })
                  }
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="form-footer">
            <button
              type="button"
              className="light-button"
              onClick={() =>
                setForm({ ...form, items: [...form.items, emptyItem()] })
              }
            >
              + Add item
            </button>
            <div>
              <span>
                Total <b>{money(total)}</b>
              </span>
              <button
                className="primary-button"
                disabled={
                  busy ||
                  (selectedBudgetLine &&
                    total >
                      Number(selectedBudgetLine.availability?.available_amount))
                }
              >
                {busy ? "Saving…" : editingId ? "Save changes" : "Save draft"}
              </button>
            </div>
          </div>
        </form>
      )}

      {activeTab === "register" && <section className="requisition-list">
        <div className="section-heading">
          <div>
            <h2>Requisition register</h2>
            <p>Your requests and requests waiting for your approval.</p>
          </div>
        </div>
        <div className="requisition-register-list">
          {rows.map((row) => {
            const budgetLine = metadata.budget_lines.find(
              (line) => String(line.id) === String(row.budget_line_id),
            );
            const approvalStage = row.approval?.status === "pending"
              ? `${row.approval.current_step?.name}${row.approval.current_step?.approver_email ? ` · ${row.approval.current_step.approver_email}` : ""}`
              : row.approval?.status || "Not submitted";
            return (
              <article className="requisition-register-item" key={row.id}>
                <div className="register-item-main">
                  <div className="register-item-heading">
                    <button className="register-number" onClick={() => open(row)}>{row.requisition_number}</button>
                    <span className={`status ${row.status}`}>{row.status}</span>
                  </div>
                  <h3>{row.purpose}</h3>
                  <p>Requested by <b>{row.requester_name}</b> on {row.request_date}</p>
                  <div className="register-item-meta">
                    <div><small>Budget account</small><b>{budgetLine?.account_name || `Line #${row.budget_line_id}`}</b></div>
                    <div><small>Approval stage</small><b>{approvalStage}</b></div>
                    <div><small>Finance status</small>
                      {row.status === "approved" && <span className="finance-state ready">Ready for payment request</span>}
                      {row.status === "converted" && <span className="finance-state linked">Payment request #{row.payment_request_id}</span>}
                      {!['approved', 'converted'].includes(row.status) && <span className="finance-state waiting">Awaiting requisition approval</span>}
                    </div>
                  </div>
                </div>
                <aside className="register-item-side">
                  <small>Requested amount</small>
                  <strong>{money(row.amount)}</strong>
                  <div className="actions">
                      <button onClick={() => open(row)}>View</button>
                      {row.status !== "draft" && <button onClick={() => requisitionPdf(row, true)}>PDF View</button>}
                      {row.status === "approved" && hasPermission("requisitions.convert") && <button className="payment-action" onClick={() => open(row)}>Create Payment Request</button>}
                      {row.status === "draft" && (
                        <>
                          <button onClick={() => edit(row)}>Edit</button>
                          <button onClick={() => action(row, "submit")}>
                            Submit
                          </button>
                          <button onClick={() => action(row, "cancel")}>
                            Cancel
                          </button>
                        </>
                      )}
                      {row.status === "submitted" &&
                        hasPermission("requisitions.approve") &&
                        (!row.approval?.current_step?.approver_user_id || String(row.approval.current_step.approver_user_id) === String(user?.id)) && (
                          <>
                            <button onClick={() => action(row, "approve")}>
                              Approve
                            </button>
                            <button onClick={() => action(row, "reject")}>
                              Reject
                            </button>
                          </>
                        )}
                      {row.status === "submitted" && (
                        <button onClick={() => action(row, "cancel")}>
                          Cancel
                        </button>
                      )}
                      {row.status === "rejected" && (
                        <button onClick={() => action(row, "return")}>
                          Return to draft
                        </button>
                      )}
                  </div>
                </aside>
              </article>
            );
          })}
          {rows.length === 0 && <div className="empty register-empty">No requisitions yet.</div>}
        </div>
      </section>}

      {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)}>
          <div
            className="requisition-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="requisition-document-header">
              <div className="document-brand"><img src={brandingLogo} alt=""/><div><b>{branding.organization_short_name}</b><small>{branding.organization_name}</small></div></div>
              <div className="document-number"><small>STAFF REQUISITION</small><strong>{selected.requisition_number}</strong><span className={`status ${selected.status}`}>{selected.status}</span></div>
              <button className="document-close" onClick={() => setSelected(null)} aria-label="Close">×</button>
            </header>
            <div className="requisition-document-toolbar"><div><span>Created {selected.request_date}</span><b>{money(selected.amount)}</b></div>{selected.status !== "draft" && <div className="requisition-pdf-actions"><button className="light-button" onClick={() => requisitionPdf(selected, true)}>Preview PDF</button><button className="primary-button" onClick={() => requisitionPdf(selected, false)}>Download PDF</button></div>}</div>
            <section className="requisition-document-section request-summary">
              <div className="document-party"><small>REQUESTED BY</small><h3>{selected.requester_name}</h3><span>{selectedBranch ? `${selectedBranch.code} — ${selectedBranch.name}` : "National"}</span></div>
              <div className="document-meta"><div><small>Required date</small><b>{selected.required_date || "Not specified"}</b></div><div><small>Suggested payee</small><b>{selected.preferred_payee || "Not specified"}</b></div><div><small>Budget account</small><b>{selectedBudgetMeta ? `${selectedBudgetMeta.account_code} — ${selectedBudgetMeta.account_name}` : `Line #${selected.budget_line_id}`}</b></div></div>
            </section>
            <section className="requisition-purpose"><small>PURPOSE</small><p>{selected.purpose}</p>{selected.procurement_notes && <><small>PROCUREMENT NOTES</small><p>{selected.procurement_notes}</p></>}</section>
            <section className="requisition-document-section"><div className="document-section-title"><span>REQUEST ITEMS</span><b>{selected.items.length} item(s)</b></div><div className="requisition-detail-table"><div className="detail-table-head"><span>#</span><span>Description</span><span>Qty</span><span>Unit price</span><span>Total</span></div>{selected.items.map((item,index) => <div className="detail-table-row" key={item.id}><span>{index+1}</span><b>{item.description}</b><span>{item.quantity} {item.unit}</span><span>{money(item.unit_price)}</span><strong>{money(item.line_total)}</strong></div>)}<div className="detail-table-total"><span>Total requested</span><strong>{money(selected.amount)}</strong></div></div></section>
            <section className="requisition-document-section"><div className="document-section-title"><span>APPROVAL TRAIL</span><b>{selected.approval?.status || "Not submitted"}</b></div>
            <div className="approval-flow">
              {selected.approval?.steps?.map((step) => {
                const decision = selected.approval.decisions?.find(
                  (d) => String(d.workflow_step_id) === String(step.id),
                );
                return (
                  <div
                    className={
                      decision?.action ||
                      (String(step.step_order) ===
                      String(selected.approval.current_step_order)
                        ? "current"
                        : "")
                    }
                    key={step.id}
                  >
                    <b>
                      {step.step_order}. {step.name}
                    </b>
                    <span>{decision?.action || "Waiting"}</span>
                  </div>
                );
              }) || <p>Submit the draft to start approval.</p>}
            </div>
            </section>
            <section className="requisition-document-section"><div className="document-section-title"><span>SUPPORTING DOCUMENTS</span><b>{documents.length}</b></div><div className="document-list">
            {documents.map((doc) => (
              <button
                className="document-link"
                key={doc.id}
                onClick={() =>
                  api.downloadStaffRequisitionDocument(
                    selected.id,
                    doc.id,
                    doc.title,
                  )
                }
              >
                {doc.title}
              </button>
            ))}
            {documents.length === 0 && <p className="no-documents">No supporting documents uploaded.</p>}
            </div>
            {hasPermission("requisitions.create") && (
              <form className="document-form" onSubmit={upload}>
                <input
                  required
                  placeholder="Document title"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                />
                <input
                  required
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setDocumentFile(e.target.files[0])}
                />
                <button>Upload</button>
              </form>
            )}
            </section>
            {selected.status === "approved" &&
              hasPermission("requisitions.convert") && (
                <form className="conversion-form" onSubmit={convert}>
                  <h3>Create Payment Request</h3>
                  <input
                    required
                    placeholder="Payee"
                    value={convertForm.payee}
                    onChange={(e) =>
                      setConvertForm({ ...convertForm, payee: e.target.value })
                    }
                  />
                  <select
                    required
                    value={convertForm.cash_account_id}
                    onChange={(e) =>
                      setConvertForm({
                        ...convertForm,
                        cash_account_id: e.target.value,
                      })
                    }
                  >
                    <option value="">Cash / bank account</option>
                    {metadata.cash_accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.code} — {account.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={convertForm.payment_method}
                    onChange={(e) =>
                      setConvertForm({
                        ...convertForm,
                        payment_method: e.target.value,
                      })
                    }
                  >
                    <option value="bank_transfer">Bank transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="mobile_money">Mobile money</option>
                  </select>
                  <input
                    required
                    type="date"
                    value={convertForm.payment_date}
                    onChange={(e) =>
                      setConvertForm({
                        ...convertForm,
                        payment_date: e.target.value,
                      })
                    }
                  />
                  <input
                    placeholder="Payment reference"
                    value={convertForm.payment_reference}
                    onChange={(e) =>
                      setConvertForm({
                        ...convertForm,
                        payment_reference: e.target.value,
                      })
                    }
                  />
                  <button className="primary-button">
                    Create payment draft
                  </button>
                </form>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
